<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TriggerNodeEditor from './components/editors/TriggerNodeEditor.vue';
import MessageNodeEditor from './components/editors/MessageNodeEditor.vue';
import BlockNodeEditor from './components/editors/BlockNodeEditor.vue';
import ConditionNodeEditor from './components/editors/ConditionNodeEditor.vue';
import ActionNodeEditor from './components/editors/ActionNodeEditor.vue';
import DataCollectionNodeEditor from './components/editors/DataCollectionNodeEditor.vue';
import DelayNodeEditor from './components/editors/DelayNodeEditor.vue';

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update', 'delete', 'close']);

const editorComponent = computed(() => {
  const map = {
    trigger: TriggerNodeEditor,
    message: MessageNodeEditor,
    block: BlockNodeEditor,
    condition: ConditionNodeEditor,
    action: ActionNodeEditor,
    data_collection: DataCollectionNodeEditor,
    delay: DelayNodeEditor,
  };
  return map[props.node.type] || null;
});

const nodeMeta = computed(() => {
  const meta = {
    trigger: {
      icon: 'i-lucide-zap',
      bgClass: 'bg-n-blue-1 dark:bg-n-blue-2',
      textClass: 'text-n-blue-11',
    },
    message: {
      icon: 'i-lucide-message-square',
      bgClass: 'bg-n-green-1 dark:bg-n-green-2',
      textClass: 'text-n-green-11',
    },
    block: {
      icon: 'i-lucide-layout-grid',
      bgClass: 'bg-n-indigo-1 dark:bg-n-indigo-2',
      textClass: 'text-n-indigo-11',
    },
    condition: {
      icon: 'i-lucide-git-branch',
      bgClass: 'bg-n-amber-1 dark:bg-n-amber-2',
      textClass: 'text-n-amber-11',
    },
    action: {
      icon: 'i-lucide-settings',
      bgClass: 'bg-n-violet-1 dark:bg-n-violet-2',
      textClass: 'text-n-violet-11',
    },
    data_collection: {
      icon: 'i-lucide-clipboard-list',
      bgClass: 'bg-n-violet-1 dark:bg-n-violet-2',
      textClass: 'text-n-violet-11',
    },
    delay: {
      icon: 'i-lucide-clock',
      bgClass: 'bg-n-amber-1 dark:bg-n-amber-2',
      textClass: 'text-n-amber-11',
    },
    group: {
      icon: 'i-lucide-box',
      bgClass: 'bg-n-slate-2 dark:bg-n-slate-3',
      textClass: 'text-n-slate-11',
    },
  };
  return meta[props.node.type] || meta.message;
});

const onUpdate = newData => {
  emit('update', props.node.id, newData);
};

const onDelete = () => {
  emit('delete', props.node.id);
};
</script>

<template>
  <div
    class="absolute left-4 top-4 bottom-4 z-20 flex flex-col rounded-xl bg-white dark:bg-n-surface-1 shadow-2xl overflow-y-auto w-[40rem]"
  >
    <div
      class="flex items-center justify-between px-4 py-3 bg-n-slate-1 dark:bg-n-slate-2 rounded-t-xl"
    >
      <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-3">
        <div
          class="w-9 h-9 rounded-xl flex items-center justify-center"
          :class="nodeMeta.bgClass"
        >
          <span class="text-lg" :class="[nodeMeta.icon, nodeMeta.textClass]" />
        </div>
        {{ node.data?.label || node.type }}
      </h3>
      <div class="flex items-center gap-1">
        <Button
          v-if="node.type !== 'trigger'"
          variant="ghost"
          size="xs"
          color="ruby"
          icon="i-lucide-trash-2"
          @click="onDelete"
        />
        <Button
          variant="ghost"
          size="xs"
          icon="i-lucide-x"
          @click="emit('close')"
        />
      </div>
    </div>
    <div class="flex-1 p-4 flex flex-col gap-4">
      <component
        :is="editorComponent"
        v-if="editorComponent"
        :data="node.data"
        :inboxes="inboxes"
        @update="onUpdate"
      />
    </div>
  </div>
</template>
